import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PlusCircle,
  Search,
  FolderKanban,
  BookOpen,
  Trash2,
  Edit,
  ExternalLink,
  Plus,
  SlidersHorizontal,
  Clock,
  Layers,
  X,
  UploadCloud,
  HardDrive,
  Download,
  CheckSquare,
  Square,
  AlertCircle,
  CheckCircle2,
  FileCheck,
  ArrowRight,
} from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { EduModal } from '../components/EduModal.js';
import { EduConfirmDialog } from '../components/EduConfirmDialog.js';
import { BatchImportModal } from '../components/BatchImportModal.js';
import { api } from '../services/api.js';
import { Project, GoogleDriveConnection, YouTubeConnection, DownloadJob } from '../types/index.js';

export const Projects: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  // Filtros e ordenação
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'name' | 'lessons'>('recent');

  // Modal de Criação / Edição
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'active' | 'archived'>('active');
  const [submitting, setSubmitting] = useState(false);

  // Modal de Exclusão
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Modal de Importação em Lote (ZIP)
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [batchProjectId, setBatchProjectId] = useState<string>('');

  // Google Drive & YouTube & Seleção Múltipla
  const [driveConnection, setDriveConnection] = useState<GoogleDriveConnection | null>(null);
  const [youtubeConnection, setYouTubeConnection] = useState<YouTubeConnection | null>(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());

  // Modal de Envio para Google Drive
  const [isDriveModalOpen, setIsDriveModalOpen] = useState(false);
  const [targetProjects, setTargetProjects] = useState<Project[]>([]);
  const [transferring, setTransferring] = useState(false);

  // Modal de Envio para YouTube
  const [isYouTubeModalOpen, setIsYouTubeModalOpen] = useState(false);
  const [targetYouTubeProjects, setTargetYouTubeProjects] = useState<Project[]>([]);
  const [transferringYouTube, setTransferringYouTube] = useState(false);
  const [youtubeAudienceClassification, setYoutubeAudienceClassification] = useState(false);

  // Modal de Download ZIP de Projeto
  const [isZipModalOpen, setIsZipModalOpen] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [activeDownloadJob, setActiveDownloadJob] = useState<DownloadJob | null>(null);

  const openBatchModal = (projId?: string) => {
    setBatchProjectId(projId || '');
    setIsBatchModalOpen(true);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [resProjects, resDrive, resYouTube] = await Promise.allSettled([
        api.getProjects(),
        api.getGoogleDriveIntegration(),
        api.getYouTubeIntegration(),
      ]);
      if (resProjects.status === 'fulfilled') {
        setProjects(resProjects.value.items || []);
      }
      if (resDrive.status === 'fulfilled') {
        setDriveConnection(resDrive.value);
      }
      if (resYouTube.status === 'fulfilled') {
        setYouTubeConnection(resYouTube.value);
      }
    } catch (err) {
      console.error('Erro ao carregar projetos:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (searchParams.get('import') === 'batch' || searchParams.get('importBatch') === 'true') {
      const projId = searchParams.get('projectId');
      if (projId) setBatchProjectId(projId);
      setIsBatchModalOpen(true);
    }
  }, [searchParams]);

  const openCreateModal = () => {
    setEditingProject(null);
    setName('');
    setSubject('');
    setDescription('');
    setStatus('active');
    setIsModalOpen(true);
  };

  const openEditModal = (proj: Project) => {
    setEditingProject(proj);
    setName(proj.name);
    setSubject(proj.subject || '');
    setDescription(proj.description || '');
    setStatus(proj.status || 'active');
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSubmitting(true);
    try {
      if (editingProject) {
        await api.updateProject(editingProject.id, {
          name: name.trim(),
          subject: subject.trim() || undefined,
          description: description.trim() || undefined,
          status,
        });
      } else {
        await api.createProject({
          name: name.trim(),
          subject: subject.trim() || undefined,
          description: description.trim() || undefined,
        });
      }
      setIsModalOpen(false);
      loadData();
    } catch (err: any) {
      alert(err.message || 'Falha ao salvar projeto.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingProject) return;
    try {
      setDeleteLoading(true);
      await api.deleteProject(deletingProject.id);
      setDeletingProject(null);
      loadData();
    } catch (err: any) {
      alert(err.message || 'Erro ao excluir projeto.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Funções de Seleção Múltipla
  const toggleSelectProject = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAllPage = () => {
    const allIds = new Set(filteredProjects.map((p) => p.id));
    setSelectedProjectIds(allIds);
  };

  const clearSelection = () => {
    setSelectedProjectIds(new Set());
  };

  // Funções de Transferência para o Google Drive
  const openDriveTransferModal = (projs: Project[]) => {
    setTargetProjects(projs);
    setIsDriveModalOpen(true);
  };

  const handleConfirmTransfer = async () => {
    if (targetProjects.length === 0) return;
    setTransferring(true);
    try {
      await api.createTransferBatch(targetProjects.map((p) => p.id));
      setIsDriveModalOpen(false);
      setSelectedProjectIds(new Set());
      navigate('/transferencias');
    } catch (err: any) {
      alert(err.message || 'Falha ao iniciar transferência para o Google Drive.');
    } finally {
      setTransferring(false);
    }
  };

  // Funções de Transferência para o YouTube
  const openYouTubeTransferModal = (projs: Project[]) => {
    setTargetYouTubeProjects(projs);
    if (youtubeConnection?.settings?.defaultAudienceClassification !== undefined) {
      setYoutubeAudienceClassification(youtubeConnection.settings.defaultAudienceClassification);
    }
    setIsYouTubeModalOpen(true);
  };

  const handleConfirmYouTubeTransfer = async () => {
    if (targetYouTubeProjects.length === 0) return;
    setTransferringYouTube(true);
    try {
      const audienceSettings: Record<string, boolean> = {};
      targetYouTubeProjects.forEach((p) => {
        audienceSettings[p.id] = youtubeAudienceClassification;
      });

      await api.createYouTubeTransferBatch(
        targetYouTubeProjects.map((p) => p.id),
        audienceSettings
      );
      setIsYouTubeModalOpen(false);
      setSelectedProjectIds(new Set());
      navigate('/transferencias?provider=youtube');
    } catch (err: any) {
      alert(err.message || 'Falha ao iniciar exportação para o YouTube.');
    } finally {
      setTransferringYouTube(false);
    }
  };

  // Download ZIP de Todo o Projeto
  const handleDownloadProjectZip = async (proj: Project) => {
    setDownloadingZip(true);
    try {
      const res = await api.createProjectDownload(proj.id);
      setActiveDownloadJob(res.job);
      setIsZipModalOpen(true);

      const checkInterval = setInterval(async () => {
        try {
          const updated = await api.getDownloadJob(res.job.id);
          setActiveDownloadJob(updated);
          if (updated.status === 'ready' || updated.status === 'error') {
            clearInterval(checkInterval);
          }
        } catch {
          clearInterval(checkInterval);
        }
      }, 2000);
    } catch (err: any) {
      alert(err.message || 'Erro ao gerar pacote ZIP das aulas do projeto.');
    } finally {
      setDownloadingZip(false);
    }
  };

  // Filtragem e ordenação
  const filteredProjects = useMemo(() => {
    return projects
      .filter((p) => {
        const matchesSearch =
          p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (p.subject && p.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesStatus =
          statusFilter === 'all' || p.status === statusFilter;

        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        if (sortBy === 'name') {
          return a.name.localeCompare(b.name);
        }
        if (sortBy === 'lessons') {
          return (b.lessonCount || 0) - (a.lessonCount || 0);
        }
        // recent: default por createdAt desc
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });
  }, [projects, searchTerm, statusFilter, sortBy]);

  // Estatísticas rápidas
  const totalLessons = useMemo(
    () => projects.reduce((acc, p) => acc + (p.lessonCount || 0), 0),
    [projects]
  );
  const activeCount = useMemo(
    () => projects.filter((p) => p.status === 'active').length,
    [projects]
  );

  return (
    <div>
      <Header
        title="Meus Projetos"
        subtitle="Organize seus cursos, disciplinas e módulos didáticos para geração de videoaulas"
        action={
          <div className="flex items-center gap-2.5">
            <EduButton
              variant="secondary"
              size="sm"
              onClick={() => openBatchModal()}
              icon={<UploadCloud size={16} className="text-brand-primary" />}
            >
              Importar Aulas (ZIP)
            </EduButton>
            <EduButton
              variant="primary"
              size="sm"
              onClick={openCreateModal}
              icon={<PlusCircle size={16} />}
            >
              Novo Projeto
            </EduButton>
          </div>
        }
      />

      <div className="p-8 max-w-7xl mx-auto space-y-6">
        {/* Métricas Rápidas */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-surface border border-bdr rounded-xl p-4 flex items-center gap-3 shadow-sm">
            <div className="p-2.5 rounded-lg bg-orange-50 text-brand-primary border border-orange-100">
              <FolderKanban size={20} />
            </div>
            <div>
              <p className="text-xs text-txt-secondary font-medium">Total de Projetos</p>
              <p className="text-xl font-bold text-txt-primary">{projects.length}</p>
            </div>
          </div>

          <div className="bg-surface border border-bdr rounded-xl p-4 flex items-center gap-3 shadow-sm">
            <div className="p-2.5 rounded-lg bg-blue-50 text-status-info border border-blue-100">
              <BookOpen size={20} />
            </div>
            <div>
              <p className="text-xs text-txt-secondary font-medium">Aulas Cadastradas</p>
              <p className="text-xl font-bold text-txt-primary">{totalLessons}</p>
            </div>
          </div>

          <div className="bg-surface border border-bdr rounded-xl p-4 flex items-center gap-3 shadow-sm">
            <div className="p-2.5 rounded-lg bg-green-50 text-status-success border border-green-100">
              <Layers size={20} />
            </div>
            <div>
              <p className="text-xs text-txt-secondary font-medium">Projetos Ativos</p>
              <p className="text-xl font-bold text-txt-primary">
                {activeCount} <span className="text-xs font-normal text-txt-muted">/ {projects.length}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Barra de Filtros e Busca */}
        <div className="bg-surface border border-bdr rounded-xl p-4 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-2.5 text-txt-muted" size={16} />
            <input
              type="text"
              placeholder="Buscar por nome, disciplina ou conteúdo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-sm bg-surface-secondary border border-bdr rounded-lg text-txt-primary placeholder:text-txt-muted focus:outline-none focus:border-brand-primary focus:bg-surface transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-2.5 text-txt-muted hover:text-txt-primary"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Filtro de Status */}
            <div className="flex items-center gap-1 bg-surface-secondary p-1 rounded-lg border border-bdr text-xs">
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-surface text-txt-primary shadow-xs font-semibold'
                    : 'text-txt-secondary hover:text-txt-primary'
                }`}
              >
                Todos
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('active')}
                className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                  statusFilter === 'active'
                    ? 'bg-surface text-txt-primary shadow-xs font-semibold'
                    : 'text-txt-secondary hover:text-txt-primary'
                }`}
              >
                Ativos
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('archived')}
                className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                  statusFilter === 'archived'
                    ? 'bg-surface text-txt-primary shadow-xs font-semibold'
                    : 'text-txt-secondary hover:text-txt-primary'
                }`}
              >
                Arquivados
              </button>
            </div>

            {/* Ordenação */}
            <div className="flex items-center gap-2 text-xs text-txt-secondary">
              <SlidersHorizontal size={14} className="text-txt-muted" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="bg-surface border border-bdr rounded-lg px-2.5 py-1.5 text-txt-primary focus:outline-none focus:border-brand-primary text-xs"
              >
                <option value="recent">Mais Recentes</option>
                <option value="name">Ordem Alfabética (A-Z)</option>
                <option value="lessons">Mais Aulas</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista de Projetos */}
        {loading ? (
          <div className="p-16 text-center text-xs text-txt-muted flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
            <span>Carregando projetos...</span>
          </div>
        ) : filteredProjects.length === 0 ? (
          <EduCard>
            <div className="text-center py-12">
              <FolderKanban size={40} className="mx-auto text-txt-muted/60 mb-3" />
              <h3 className="text-base font-semibold text-txt-primary">Nenhum projeto encontrado</h3>
              <p className="text-xs text-txt-secondary mt-1 max-w-sm mx-auto">
                {searchTerm || statusFilter !== 'all'
                  ? 'Nenhum resultado corresponde aos filtros selecionados. Tente ajustar a busca.'
                  : 'Comece criando seu primeiro projeto para organizar suas aulas e materiais didáticos.'}
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <EduButton
                  variant="primary"
                  size="sm"
                  className="mt-5"
                  onClick={openCreateModal}
                  icon={<PlusCircle size={15} />}
                >
                  Criar Primeiro Projeto
                </EduButton>
              )}
            </div>
          </EduCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((proj) => (
              <div
                key={proj.id}
                className="bg-surface border border-bdr rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex flex-col overflow-hidden group"
              >
                {/* Header do Card */}
                <div className="p-5 border-b border-bdr/60 bg-gradient-to-b from-surface-secondary/40 to-transparent">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2.5 flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={(e) => toggleSelectProject(proj.id, e)}
                        className="mt-0.5 p-1 rounded text-txt-muted hover:text-brand-primary hover:bg-orange-50 transition-colors shrink-0"
                        title={selectedProjectIds.has(proj.id) ? 'Desmarcar projeto' : 'Selecionar projeto para envio'}
                      >
                        {selectedProjectIds.has(proj.id) ? (
                          <CheckSquare size={18} className="text-brand-primary" />
                        ) : (
                          <Square size={18} className="text-txt-muted/70" />
                        )}
                      </button>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <EduBadge
                            variant={proj.status === 'active' ? 'success' : 'neutral'}
                            size="sm"
                            dot
                          >
                            {proj.status === 'active' ? 'Ativo' : 'Arquivado'}
                          </EduBadge>
                          {proj.subject && (
                            <span className="text-[11px] font-medium text-txt-muted bg-surface-secondary px-2 py-0.5 rounded border border-bdr/80 truncate">
                              {proj.subject}
                            </span>
                          )}
                        </div>
                        <h3
                          className="text-base font-bold text-txt-primary truncate group-hover:text-brand-primary transition-colors cursor-pointer"
                          onClick={() => navigate(`/aulas?projectId=${proj.id}`)}
                          title={proj.name}
                        >
                          {proj.name}
                        </h3>
                      </div>
                    </div>

                    {/* Ações de Edição e Exclusão */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEditModal(proj)}
                        className="p-1.5 text-txt-muted hover:text-brand-primary hover:bg-orange-50 rounded-lg transition-colors"
                        title="Editar Projeto"
                      >
                        <Edit size={15} />
                      </button>
                      <button
                        onClick={() => setDeletingProject(proj)}
                        className="p-1.5 text-txt-muted hover:text-status-error hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir Projeto"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Conteúdo do Card */}
                <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                  <p className="text-xs text-txt-secondary line-clamp-3 leading-relaxed">
                    {proj.description || 'Nenhuma descrição adicionada para este projeto.'}
                  </p>

                  <div className="pt-2 flex items-center justify-between border-t border-bdr/60 text-xs">
                    <span className="flex items-center gap-1.5 font-semibold text-txt-primary">
                      <BookOpen size={14} className="text-brand-primary" />
                      {proj.lessonCount || 0} {proj.lessonCount === 1 ? 'aula' : 'aulas'}
                    </span>
                    {proj.createdAt && (
                      <span className="flex items-center gap-1 text-[11px] text-txt-muted">
                        <Clock size={12} />
                        {new Date(proj.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Rodapé com Links de Ação */}
                <div className="p-3 bg-surface-secondary/70 border-t border-bdr flex flex-col gap-2.5">
                  <div className="flex items-center justify-between gap-1.5">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => navigate(`/nova-videoaula?projectId=${proj.id}`)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-txt-secondary hover:text-brand-primary transition-colors px-2 py-1 rounded hover:bg-surface"
                        title="Criar aula manual"
                      >
                        <Plus size={13} />
                        Nova Aula
                      </button>

                      <button
                        onClick={() => openBatchModal(proj.id)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-brand-primary hover:text-brand-hover hover:bg-orange-50 transition-colors px-2 py-1 rounded border border-orange-200"
                        title="Importar múltiplas aulas deste projeto via arquivo ZIP"
                      >
                        <UploadCloud size={13} />
                        ZIP
                      </button>
                    </div>

                    <button
                      onClick={() => navigate(`/aulas?projectId=${proj.id}`)}
                      className="inline-flex items-center gap-1 text-xs font-bold text-txt-primary hover:text-brand-primary transition-colors px-2.5 py-1 rounded hover:bg-surface"
                    >
                      Aulas
                      <ExternalLink size={13} />
                    </button>
                  </div>

                  {/* Ações Específicas: Google Drive, YouTube e Download ZIP */}
                  <div className="pt-2 border-t border-bdr/60 flex items-center justify-between gap-1 flex-wrap">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openDriveTransferModal([proj])}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold text-brand-primary hover:bg-orange-50 border border-orange-200 transition-colors"
                        title="Enviar videoaulas concluídas deste projeto para o Google Drive"
                      >
                        <HardDrive size={12} />
                        Drive
                      </button>

                      <button
                        onClick={() => openYouTubeTransferModal([proj])}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold text-red-600 hover:bg-red-50 border border-red-200 transition-colors"
                        title="Exportar videoaulas deste projeto para playlist não listada no YouTube"
                      >
                        <svg className="w-3.5 h-3.5 text-[#FF0000]" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                        </svg>
                        YouTube
                      </button>
                    </div>

                    <button
                      onClick={() => handleDownloadProjectZip(proj)}
                      disabled={downloadingZip}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold text-txt-secondary hover:text-txt-primary hover:bg-surface border border-bdr transition-colors"
                      title="Baixar pacote ZIP de todas as aulas deste projeto"
                    >
                      <Download size={12} />
                      ZIP
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Barra Flutuante de Seleção Múltipla */}
        {selectedProjectIds.size > 0 && (
          <div className="sticky bottom-6 z-20 bg-graphite text-white rounded-2xl p-4 shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-4 border border-slate-700 animate-in fade-in slide-in-from-bottom-4">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-brand-primary text-white flex items-center justify-center font-bold text-xs shrink-0">
                {selectedProjectIds.size}
              </span>
              <div>
                <p className="text-xs font-bold text-white">
                  {selectedProjectIds.size} {selectedProjectIds.size === 1 ? 'projeto selecionado' : 'projetos selecionados'}
                </p>
                <p className="text-[11px] text-slate-300">
                  Pronto para exportar para o Google Drive ou canal do YouTube
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={clearSelection}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
              >
                Limpar Seleção
              </button>
              <button
                onClick={selectAllPage}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
              >
                Selecionar Todos ({filteredProjects.length})
              </button>
              <EduButton
                variant="primary"
                size="sm"
                onClick={() => {
                  const selected = projects.filter((p) => selectedProjectIds.has(p.id));
                  openDriveTransferModal(selected);
                }}
                icon={<HardDrive size={15} />}
              >
                Enviar ao Drive
              </EduButton>
              <EduButton
                variant="secondary"
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white border-none"
                onClick={() => {
                  const selected = projects.filter((p) => selectedProjectIds.has(p.id));
                  openYouTubeTransferModal(selected);
                }}
                icon={
                  <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                }
              >
                Enviar ao YouTube
              </EduButton>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Criação / Edição */}
      <EduModal
        isOpen={isModalOpen}
        onClose={() => !submitting && setIsModalOpen(false)}
        title={editingProject ? 'Editar Projeto' : 'Novo Projeto'}
        subtitle={
          editingProject
            ? 'Atualize as informações e o status do seu projeto'
            : 'Preencha os dados para iniciar um novo curso ou disciplina'
        }
        maxWidth="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-txt-primary mb-1">
              Nome do Projeto / Curso <span className="text-status-error">*</span>
            </label>
            <input
              type="text"
              required
              placeholder="Ex: Direito Constitucional para Concursos"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary bg-surface"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-txt-primary mb-1">
              Disciplina / Área Temática
            </label>
            <input
              type="text"
              placeholder="Ex: Direito Público, Matemática Básica, Gestão"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary bg-surface"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-txt-primary mb-1">
              Descrição do Projeto
            </label>
            <textarea
              rows={3}
              placeholder="Breve resumo sobre o público-alvo e conteúdo deste curso..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary bg-surface resize-none"
            />
          </div>

          {editingProject && (
            <div>
              <label className="block text-xs font-semibold text-txt-primary mb-1">
                Status do Projeto
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as any)}
                className="w-full px-3 py-2 text-sm border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary bg-surface"
              >
                <option value="active">Ativo (visível e disponível)</option>
                <option value="archived">Arquivado (somente leitura)</option>
              </select>
            </div>
          )}

          <div className="flex justify-end gap-2.5 pt-4 border-t border-bdr">
            <EduButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setIsModalOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </EduButton>
            <EduButton
              type="submit"
              variant="primary"
              size="sm"
              isLoading={submitting}
            >
              {editingProject ? 'Salvar Alterações' : 'Criar Projeto'}
            </EduButton>
          </div>
        </form>
      </EduModal>

      {/* Confirmação de Exclusão */}
      <EduConfirmDialog
        isOpen={!!deletingProject}
        onClose={() => setDeletingProject(null)}
        onConfirm={confirmDelete}
        title="Excluir Projeto"
        description={
          <span>
            Tem certeza que deseja excluir o projeto{' '}
            <strong>"{deletingProject?.name}"</strong>? Esta ação removerá o projeto e
            não poderá ser desfeita.
          </span>
        }
        confirmLabel="Sim, excluir projeto"
        cancelLabel="Cancelar"
        variant="danger"
        isLoading={deleteLoading}
      />

      {/* Modal de Importação em Lote de Aulas (ZIP) */}
      <BatchImportModal
        isOpen={isBatchModalOpen}
        onClose={() => setIsBatchModalOpen(false)}
        projects={projects}
        initialProjectId={batchProjectId}
        onSuccess={(projId) => {
          loadData();
          navigate(`/aulas?projectId=${projId}`);
        }}
      />

      {/* Modal de Confirmação de Transferência para o Google Drive */}
      <EduModal
        isOpen={isDriveModalOpen}
        onClose={() => !transferring && setIsDriveModalOpen(false)}
        title={`Enviar ${targetProjects.length} ${targetProjects.length === 1 ? 'projeto' : 'projetos'} ao Google Drive`}
        subtitle="Confirme os detalhes da transferência e organização no seu Drive"
        maxWidth="md"
      >
        <div className="space-y-4">
          {/* Validação de Conexão */}
          {(!driveConnection || driveConnection.connectionStatus === 'not_configured' || driveConnection.connectionStatus === 'disconnected') ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
              <div className="flex items-center gap-2 text-amber-800 font-bold text-xs">
                <AlertCircle size={16} /> Conta Google Drive não conectada
              </div>
              <p className="text-xs text-amber-700">
                Você precisa conectar e autorizar sua conta Google antes de enviar videoaulas.
              </p>
              <EduButton
                variant="primary"
                size="sm"
                onClick={() => {
                  setIsDriveModalOpen(false);
                  navigate('/minha-conta?tab=integracoes');
                }}
                className="mt-2"
              >
                Conectar Google Drive
              </EduButton>
            </div>
          ) : (
            <>
              {/* Resumo da Transferência conforme Seção 6.3 */}
              <div className="bg-surface-secondary rounded-xl p-4 border border-bdr space-y-2.5 text-xs">
                <div className="flex items-center justify-between pb-2 border-b border-bdr">
                  <span className="text-txt-muted">Conta de Destino</span>
                  <span className="font-semibold text-txt-primary flex items-center gap-1">
                    <CheckCircle2 size={13} className="text-status-success" />
                    {driveConnection.googleAccountEmail}
                  </span>
                </div>

                <div className="flex items-center justify-between pb-2 border-b border-bdr">
                  <span className="text-txt-muted">Pasta Principal</span>
                  <span className="font-semibold text-txt-primary font-mono">
                    Google Drive / {driveConnection.rootFolderName || 'EduRender'}
                  </span>
                </div>

                <div className="flex items-center justify-between pb-2 border-b border-bdr">
                  <span className="text-txt-muted">Projetos Selecionados</span>
                  <span className="font-bold text-txt-primary">
                    {targetProjects.length} ({targetProjects.map((p) => p.name).join(', ')})
                  </span>
                </div>

                <div className="flex items-center justify-between pb-2 border-b border-bdr">
                  <span className="text-txt-muted">Vídeos MP4 Disponíveis (Estimativa)</span>
                  <span className="font-bold text-txt-primary text-status-success">
                    {targetProjects.reduce((acc, p) => acc + (p.lessonCount || 0), 0)} vídeos
                  </span>
                </div>

                <div className="flex items-center justify-between pb-2 border-b border-bdr">
                  <span className="text-txt-muted">Volume Total Estimado</span>
                  <span className="font-mono text-txt-primary">
                    ~{(targetProjects.reduce((acc, p) => acc + (p.lessonCount || 0), 0) * 120 / 1024).toFixed(1)} GB
                  </span>
                </div>

                <div className="flex items-start justify-between pt-1">
                  <span className="text-txt-muted">Política de Duplicidade</span>
                  <span className="text-txt-secondary text-right max-w-[220px]">
                    Não reenviar arquivos idênticos já presentes no Drive
                  </span>
                </div>
              </div>

              {/* Informações Importantes */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-900 space-y-1">
                <p className="font-semibold flex items-center gap-1">
                  <FileCheck size={14} className="text-status-info" /> Organização Automática:
                </p>
                <p className="text-[11px] leading-relaxed text-blue-800">
                  Cada projeto receberá sua própria pasta dentro de <strong>{driveConnection.rootFolderName || 'EduRender'}</strong>.
                  Os vídeos serão enviados em segundo plano mesmo se você fechar o navegador.
                </p>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-bdr">
                <EduButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsDriveModalOpen(false)}
                  disabled={transferring}
                >
                  Cancelar
                </EduButton>
                <EduButton
                  variant="primary"
                  size="sm"
                  onClick={handleConfirmTransfer}
                  isLoading={transferring}
                  icon={<HardDrive size={14} />}
                >
                  Confirmar Transferência
                </EduButton>
              </div>
            </>
          )}
        </div>
      </EduModal>

      {/* Modal de Confirmação de Transferência para o YouTube */}
      <EduModal
        isOpen={isYouTubeModalOpen}
        onClose={() => !transferringYouTube && setIsYouTubeModalOpen(false)}
        title={`Enviar ${targetYouTubeProjects.length} ${targetYouTubeProjects.length === 1 ? 'projeto' : 'projetos'} ao YouTube`}
        subtitle="Confirme os detalhes da exportação, visibilidade e classificação de público"
        maxWidth="md"
      >
        <div className="space-y-4">
          {/* Validação de Conexão */}
          {(!youtubeConnection || youtubeConnection.connectionStatus === 'not_configured' || youtubeConnection.connectionStatus === 'disconnected') ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
              <div className="flex items-center gap-2 text-amber-800 font-bold text-xs">
                <AlertCircle size={16} /> Canal do YouTube não conectado
              </div>
              <p className="text-xs text-amber-700">
                Você precisa conectar e autorizar seu canal do YouTube antes de enviar videoaulas.
              </p>
              <EduButton
                variant="primary"
                size="sm"
                onClick={() => {
                  setIsYouTubeModalOpen(false);
                  navigate('/minha-conta?tab=youtube');
                }}
                className="mt-2 bg-red-600 hover:bg-red-700 text-white border-none"
              >
                Conectar YouTube
              </EduButton>
            </div>
          ) : youtubeConnection.connectionStatus !== 'valid' ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
              <div className="flex items-center gap-2 text-amber-800 font-bold text-xs">
                <AlertCircle size={16} /> Conexão do YouTube pendente de teste
              </div>
              <p className="text-xs text-amber-700">
                A conexão precisa ser testada e validada antes de qualquer transferência.
              </p>
              <EduButton
                variant="primary"
                size="sm"
                onClick={() => {
                  setIsYouTubeModalOpen(false);
                  navigate('/minha-conta?tab=youtube');
                }}
                className="mt-2"
              >
                Testar Conexão em Minha Conta
              </EduButton>
            </div>
          ) : (
            <>
              {/* Resumo da Exportação conforme Seção 12 */}
              <div className="bg-surface-secondary rounded-xl p-4 border border-bdr space-y-2.5 text-xs">
                <div className="flex items-center justify-between pb-2 border-b border-bdr">
                  <span className="text-txt-muted">Canal de Destino</span>
                  <span className="font-semibold text-txt-primary flex items-center gap-2">
                    {youtubeConnection.channelThumbnail && (
                      <img
                        src={youtubeConnection.channelThumbnail}
                        alt=""
                        className="w-5 h-5 rounded-full object-cover"
                      />
                    )}
                    <CheckCircle2 size={13} className="text-status-success" />
                    {youtubeConnection.channelTitle}
                  </span>
                </div>

                <div className="flex items-center justify-between pb-2 border-b border-bdr">
                  <span className="text-txt-muted">Projetos Selecionados</span>
                  <span className="font-bold text-txt-primary truncate max-w-[280px]">
                    {targetYouTubeProjects.length} ({targetYouTubeProjects.map((p) => p.name).join(', ')})
                  </span>
                </div>

                <div className="flex items-center justify-between pb-2 border-b border-bdr">
                  <span className="text-txt-muted">Videoaulas Disponíveis (MP4)</span>
                  <span className="font-bold text-txt-primary text-status-success">
                    {targetYouTubeProjects.reduce((acc, p) => acc + (p.lessonCount || 0), 0)} vídeos
                  </span>
                </div>

                <div className="flex items-center justify-between pb-2 border-b border-bdr">
                  <span className="text-txt-muted">Playlists a Criar ou Reutilizar</span>
                  <span className="font-semibold text-txt-primary">
                    {targetYouTubeProjects.length} {targetYouTubeProjects.length === 1 ? 'playlist' : 'playlists'} (Não listadas)
                  </span>
                </div>

                <div className="flex items-center justify-between pb-2 border-b border-bdr">
                  <span className="text-txt-muted">Visibilidade dos Vídeos</span>
                  <span className="font-bold text-txt-primary px-2 py-0.5 rounded bg-slate-100 border border-slate-200">
                    Privados (private)
                  </span>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <span className="text-txt-muted">Volume Total Estimado</span>
                  <span className="font-mono text-txt-primary">
                    ~{(targetYouTubeProjects.reduce((acc, p) => acc + (p.lessonCount || 0), 0) * 120 / 1024).toFixed(1)} GB
                  </span>
                </div>
              </div>

              {/* Seletor de Público Infantil Obrigatório */}
              <div className="p-3.5 bg-surface border border-bdr rounded-xl space-y-2">
                <p className="text-xs font-bold text-txt-primary flex items-center gap-1.5">
                  <FileCheck size={14} className="text-brand-primary" /> Classificação de Público Infantil (COPPA):
                </p>
                <div className="space-y-2 pt-1 text-xs">
                  <label className="flex items-start gap-2.5 p-2.5 rounded-lg border border-bdr hover:bg-surface-secondary cursor-pointer">
                    <input
                      type="radio"
                      name="ytModalAudience"
                      checked={!youtubeAudienceClassification}
                      onChange={() => setYoutubeAudienceClassification(false)}
                      className="mt-0.5 text-brand-primary"
                    />
                    <div>
                      <span className="font-semibold block text-txt-primary">
                        Não feito especificamente para crianças (Recomendado)
                      </span>
                      <span className="text-[11px] text-txt-secondary block">
                        Para público em geral, universitários, adolescentes e profissionais.
                      </span>
                    </div>
                  </label>

                  <label className="flex items-start gap-2.5 p-2.5 rounded-lg border border-bdr hover:bg-surface-secondary cursor-pointer">
                    <input
                      type="radio"
                      name="ytModalAudience"
                      checked={youtubeAudienceClassification}
                      onChange={() => setYoutubeAudienceClassification(true)}
                      className="mt-0.5 text-brand-primary"
                    />
                    <div>
                      <span className="font-semibold block text-txt-primary">
                        Feito para crianças
                      </span>
                      <span className="text-[11px] text-txt-secondary block">
                        Aulas destinadas especificamente a crianças pequenas ou educação infantil.
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Aviso Importante de Privacidade */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 space-y-1">
                <p className="font-semibold flex items-center gap-1">
                  <AlertCircle size={14} className="text-amber-700" /> Aviso de Privacidade:
                </p>
                <p className="text-[11px] leading-relaxed text-amber-800">
                  Os vídeos serão enviados como <strong>privados</strong>. O link da playlist não concede automaticamente
                  acesso aos vídeos para outros espectadores.
                </p>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-bdr">
                <EduButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsYouTubeModalOpen(false)}
                  disabled={transferringYouTube}
                >
                  Cancelar
                </EduButton>
                <EduButton
                  variant="primary"
                  size="sm"
                  onClick={handleConfirmYouTubeTransfer}
                  isLoading={transferringYouTube}
                  className="bg-red-600 hover:bg-red-700 text-white border-none shadow-sm"
                  icon={
                    <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                  }
                >
                  Confirmar Transferência ao YouTube
                </EduButton>
              </div>
            </>
          )}
        </div>
      </EduModal>

      {/* Modal de Download ZIP do Projeto */}
      <EduModal
        isOpen={isZipModalOpen}
        onClose={() => !downloadingZip && setIsZipModalOpen(false)}
        title="Download de Vídeos do Projeto (ZIP)"
        subtitle="Empacotamento assíncrono de todas as videoaulas finalizadas"
        maxWidth="sm"
      >
        <div className="space-y-4 text-center py-2">
          {activeDownloadJob?.status === 'ready' ? (
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-full bg-green-50 text-status-success mx-auto flex items-center justify-center">
                <CheckCircle2 size={24} />
              </div>
              <h4 className="text-sm font-bold text-txt-primary">Pacote ZIP Pronto para Baixar!</h4>
              <p className="text-xs text-txt-secondary">
                O arquivo contém <strong>{activeDownloadJob.fileCount} videoaulas</strong> em formato MP4.
              </p>
              <div className="pt-2">
                <a
                  href={api.getDownloadJobFileUrl(activeDownloadJob.id)}
                  download
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-brand-primary text-white font-bold text-sm hover:bg-brand-hover shadow-sm transition-colors"
                >
                  <Download size={16} /> Baixar Arquivo ZIP
                </a>
              </div>
            </div>
          ) : activeDownloadJob?.status === 'error' ? (
            <div className="space-y-2">
              <AlertCircle size={24} className="text-status-error mx-auto" />
              <h4 className="text-xs font-bold text-status-error">Erro ao Gerar Pacote</h4>
              <p className="text-xs text-txt-muted">{activeDownloadJob.errorMessage || 'Falha no empacotamento.'}</p>
            </div>
          ) : (
            <div className="space-y-3 py-4">
              <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto" />
              <h4 className="text-xs font-bold text-txt-primary">Preparando Pacote ZIP no Servidor...</h4>
              <p className="text-[11px] text-txt-secondary max-w-xs mx-auto">
                Compactando os vídeos MP4. Isso não consome franquia e não reconverte o vídeo.
              </p>
            </div>
          )}
        </div>
      </EduModal>
    </div>
  );
};
